/* ********************************** */
/* Plannings to be used in dashboards */
/* data can be altered from original  */
/* do not use in planning page        */
/* ********************************** */

import { defineStore, acceptHMRUpdate } from 'pinia';
import Planning from '@/models/Planning';
import ProjectSrv from '@/components/Projects/ProjectSrv';
import useUsersAccessRightStore from '@/stores/users/accessRight';

function formatElProjectData(el, planning, lanesMap) {
  // remove non applicable data
  if ((el.getDependencies() || []).length) el.originalHasDependencies = true;
  el.setDependencies(null);
  el.setTimeBar(null);

  // add project meta data to elements
  el.display_title = planning.getTitle() || ""; // element tooltip
  const lane = lanesMap.get(el.getLaneId());
  if (lane?.label) el.display_title += `\n↪ ${lane.label}`;
}

function updateOrCreatePlanning({ planning = null, projectContent }) {
  // status readonly (remove if handled by api in the future)
  const projectStatusConfig = useUsersAccessRightStore().config.projectStatus || {};
  const status = projectContent?.meta?.status || '';
  if (projectStatusConfig[status]?.readonly) projectContent.meta.access_right = 'consult';

  // exclude macro elements
  projectContent.elements = projectContent.elements.filter(el => el.type != 'macro');

  if (planning) { // keep ref for multiprojects state.projects = plannings
    const lanesMap = new Map(planning.lanes.map(lane => [lane.id, lane]));
    planning.elements.forEach((el) => {
      el.dashboard_id = el.id;
      el.id = el.o_id;
    });
    planning.update(projectContent);
    planning.elements.forEach((el) => {
      if (el.dashboard_id) {
        el.id = el.dashboard_id;
      } else { // new el
        el.o_id = el.id;
        el.id = `${el.id}.${window.uuid()}`;
        el.project_id = planning.id;
      }
      delete el.dashboard_id;

      formatElProjectData(el, planning, lanesMap);
    });
  } else {
    planning = new Planning(projectContent);
    const lanesMap = new Map(planning.lanes.map(lane => [lane.id, lane]));
    planning.elements.forEach((el) => {
      el.o_id = el.id; // save original id -> should replace the parseInt
      el.id = `${el.id}.${window.uuid()}`;
      el.project_id = planning.id;

      formatElProjectData(el, planning, lanesMap);
    });
  }
  return planning;
}

const store = defineStore('multiprojects-plannings', {
  state: () => ({
    byId: {},
    loadingCounter: { toLoad: 0, loaded: 0, loadingDate: null, allPlanningsList: [] },
  }),

  actions: {
    addElementToPlanning({ planningId, el }) {
      const planning = this.byId[planningId];
      if (! planning) return;
      el.o_id = el.id;
      el.id = `${el.id}.${window.uuid()}`;
      el.project_id = planning.id;
      planning.elements.push(el);
    },

    // load is now only used in MonitoringPlan
    load({ planningId, rotoken }) {
      return ProjectSrv.get(planningId, rotoken).then((projectContent) => {
        this.byId[planningId] = updateOrCreatePlanning({ projectContent }); // regenerate fresh new Planning to avoid potential bugs
        return this.byId[planningId];
      });
    },

    loadAllPlannings() {
      this.loadingCounter.toLoad = 0;
      this.loadingCounter.loaded = 0;
      const projectStatusConfig = useUsersAccessRightStore().config.projectStatus || {};
      return ProjectSrv.getAll({ modifiedSince: this.loadingCounter.loadingDate, loadingCounter: this.loadingCounter }).then((projectContents) => {
        const nextById = { ...this.byId };
        projectContents.forEach((projectContent) => {
          const planningId = projectContent.id;
          if (projectStatusConfig[projectContent.meta.status]?.nodashboards) return;
          nextById[planningId] = updateOrCreatePlanning({ projectContent }); // regenerate fresh new Planning to avoid potential bugs
        });
        const dashboardPlannings = [];
        this.loadingCounter.allPlanningsList.forEach((planningId) => {
          const planning = nextById[planningId];
          if (! planning || projectStatusConfig[planning.meta.status]?.nodashboards) return;
          dashboardPlannings.push(planning);
        });
        this.byId = nextById;
        return dashboardPlannings; // [all plannings with status != nodashboards, macro elements excluded]
      });
    },

    loadPlanningsQuery(params) {
      return ProjectSrv.getPlanningsQuery(params).then((projectContents) => {
        const projectStatusConfig = useUsersAccessRightStore().config.projectStatus || {};
        const queryDashboardPlannings = [];
        const nextById = { ...this.byId };
        projectContents.forEach((projectContent) => {
          if (projectStatusConfig[projectContent.meta.status]?.nodashboards) return;
          const planningId = projectContent.id;
          nextById[planningId] = updateOrCreatePlanning({ planning: nextById[planningId], projectContent });
          queryDashboardPlannings.push(nextById[planningId]);
        });
        this.byId = nextById;
        return queryDashboardPlannings; // [query plannings with status != nodashboards, macro elements excluded]
      });
    },
  },
});

if (import.meta.hot) import.meta.hot.accept(acceptHMRUpdate(store, import.meta.hot));
export default store;
